import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Avatar } from "@jkast90/kast-common/ui";
import styles from "./ProfileHover.module.css";

/** Milliseconds of hover before the card shows, so passing the pointer over a row doesn't flash it. */
const DELAY_MS = 250;
const MARGIN = 8;

interface ProfileHoverProps {
  /** Whose card: shown beside their picture. */
  name: string;
  /** Their picture, if they have one; initials otherwise. */
  src: string | null | undefined;
  /** The small avatar (or badge) the card belongs to. */
  children: ReactNode;
}

/**
 * Hovering (with a mouse) or focusing what's inside shows a small profile
 * card: a larger picture and the name. A touch screen has no hover, so it
 * never shows there; the picture is a link to the profile where it is one.
 */
export function ProfileHover({ name, src, children }: ProfileHoverProps) {
  const target = useRef<HTMLSpanElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const id = useId();

  const show = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), DELAY_MS);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setOpen(false);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Escape closes it.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Above the picture, centred, kept on screen; below it when there is no room.
  // In the browser's top layer, so a dialog never covers it.
  useLayoutEffect(() => {
    const el = card.current;
    const anchor = target.current;
    if (!open || !el || !anchor) return;
    if (typeof el.showPopover === "function") el.showPopover();
    const rect = anchor.getBoundingClientRect();
    const { width, height } = el.getBoundingClientRect();
    const left = Math.min(
      Math.max(MARGIN, rect.left + rect.width / 2 - width / 2),
      Math.max(MARGIN, window.innerWidth - width - MARGIN),
    );
    const above = rect.top - height - MARGIN;
    el.style.left = `${left}px`;
    el.style.top = `${above >= MARGIN ? above : rect.bottom + MARGIN}px`;
    el.style.visibility = "visible";
  }, [open]);

  return (
    <span
      ref={target}
      className={styles.target}
      aria-describedby={open ? id : undefined}
      onPointerEnter={(event) => event.pointerType === "mouse" && show()}
      onPointerLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={card}
            id={id}
            role="tooltip"
            popover="manual"
            className={styles.card}
          >
            <Avatar name={name} src={src} size="lg" />
            <strong className={styles.name}>{name}</strong>
          </div>,
          document.body,
        )}
    </span>
  );
}
